import Stack from '@mui/material/Stack';
import useAppState from '../../../common/state/appState';
import { KmxContainer } from '../../../components/KmxContainer';
import HomePageExperience from '../hero/store/HomePageExperience';
import Typography from '@mui/material/Typography';
import { useEffect, useState, MouseEvent } from 'react';
import WhatKindOfCars from './what-kind-of-cars';
import { ANALYTICS_CONSTANTS, trackAnchorClick } from '../../../utilities/analytics';
import GlobalStyles from '@mui/material/GlobalStyles';
import { CarouselSkipLink } from '../../../components/CarouselSkipLink';

export interface IVehicleType {
    displayName: string;
    displayOrder: number;
    iconType: string;
    iconUrl: string;
    url: string;
}

export interface IWhatKindOfCarsData {
    displayOrder: number;
    componentType: string;
    componentKey: string;
    displayTitle: string;
    vehicleTypes: IVehicleType[];
}

const onCardClick = (href: string, cardName: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    trackAnchorClick({
        clickEvent: e,
        taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.SEARCH_INITIATED,
        title: cardName,
        position: 'Shop popular body styles',
        href: href,
        page: 'Homepage',
        search: {
            initiationMethod: `Homepage|popular body types|Homepage`,
        },
    });
};

const WhatKindOfCarsContainer = () => {
    const [data, setData] = useState<IWhatKindOfCarsData | null>(null);

    const experience = useAppState((state) => state.experience);
    const serveUFTContentEnabled = useAppState((state) => state.serveUFTContent);
    const visitorId = useAppState((state) => state.visitorId);
    const storeId = useAppState((state) => state.storeId);

    const isReturnVisit = experience !== HomePageExperience.FirstTimeVisitor;
    const showComponent = serveUFTContentEnabled && experience === HomePageExperience.FirstTimeVisitor;

    useEffect(() => {
        if (!showComponent || !visitorId || !storeId) {
            return;
        }

        const fetchVehicleTypes = async () => {
            try {
                const searchParams = new URLSearchParams();
                searchParams.set('PageType', 'Home');
                searchParams.set('IsReturnVisit', isReturnVisit ? 'true' : 'false');
                searchParams.set('VisitorId', visitorId);
                searchParams.set('StoreId', storeId);

                const response = await fetch(`/home/api/vehicle-types?${searchParams.toString()}`);
                if (response.ok) {
                    const result = await response.json();
                    setData(result);
                }
            } catch (error) {
                console.error('Failed to fetch vehicle types:', error);
            }
        };

        fetchVehicleTypes();
    }, [showComponent, isReturnVisit, visitorId, storeId]);

    if (!showComponent || !data) {
        return <GlobalStyles styles={{ '#what-kind-of-cars': { display: 'none' } }} />;
    }

    return (
        <KmxContainer
            maxWidth="xl"
            sx={{
                mt: { xs: 5, sm: 6 },
            }}
        >
            <Stack>
                <Typography component="h2" sx={{ typography: { xs: 'h5', sm: 'h4' } }}>
                    Shop popular body styles
                </Typography>
                <CarouselSkipLink
                    id="skip-link__what-kind-of-car"
                    href="#car-recommendations-carousel"
                    text="Skip Popular Body Styles"
                />
                <WhatKindOfCars data={data} handleClick={onCardClick} />
            </Stack>
        </KmxContainer>
    );
};

export default WhatKindOfCarsContainer;
